import { useDraggable } from '@dnd-kit/react'
import { ArrowRightLeft, Clock, ExternalLink, GripVertical, Lock } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { CalendarActivity } from '@/api/resourceCalendarApi'
import {
  ACTIVITY_STYLES,
  STATE_LABELS,
  STATE_TAGS,
  canDrag,
  formatDueMonth,
  formatHours,
  isOverdue,
  timeLabel,
} from '@/lib/myTasks'
import { cn } from '@/lib/utils'

/** The two letters on their colour — never the colour alone. */
export function TypeBadge({ code, className }: { code: CalendarActivity['type']; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex h-[18px] min-w-[1.6rem] shrink-0 items-center justify-center rounded-[5px] px-1 text-[10px] font-bold tracking-wide',
        ACTIVITY_STYLES[code].badge,
        className,
      )}
    >
      {code}
    </span>
  )
}

/** What a draggable card carries to a drop. */
export interface CardDragData {
  key: string
}

const TAG_TONE: Record<CalendarActivity['state'], string> = {
  PLANNED: '',
  IN_PROGRESS: 'bg-primary/15 text-primary',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  CANCELLED: 'bg-muted text-muted-foreground',
}

const pill = 'inline-flex h-[18px] items-center rounded-full px-1.5 text-[10px] font-medium tabular-nums'

/**
 * One activity, tinted by its type. Read top to bottom: what it is (type, state,
 * a two-line title), what it is about (the record ID, a link straight to it,
 * and its time), then the numbers as pills — due month, hours, and Late when it
 * is. A grip says the card can be dragged; a lock says it is planned in its
 * own screen. Move and Open sit on the card itself, on hover or focus.
 */
export function ActivityCard({
  activity,
  canManage,
  today,
  compact,
  onOpen,
  onMove,
}: {
  activity: CalendarActivity
  canManage: boolean
  today: string
  compact?: boolean
  onOpen: (activity: CalendarActivity) => void
  /** Change day or owner without dragging. */
  onMove?: (activity: CalendarActivity) => void
}) {
  const draggable = canDrag(activity, canManage)
  const { ref, isDragging } = useDraggable({
    id: activity.key,
    data: { key: activity.key } satisfies CardDragData,
    disabled: !draggable,
  })
  const done = activity.state === 'COMPLETED' || activity.state === 'CANCELLED'
  const late = isOverdue(activity, today)
  const tag = STATE_TAGS[activity.state]
  const time = timeLabel(activity)
  const style = ACTIVITY_STYLES[activity.type]
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()

  return (
    // A link sits inside the card, so the card is a focusable element rather
    // than a <button> (a link inside a button is invalid and unclickable).
    <div
      ref={ref}
      role='button'
      tabIndex={0}
      aria-label={`${activity.type} ${activity.description}${late ? ', overdue' : ''}`}
      onClick={() => onOpen(activity)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen(activity)
        }
      }}
      title={[activity.description, activity.sourceRef, time, `${formatHours(activity.hours)} h`, late ? 'Overdue' : null, STATE_LABELS[activity.state]]
        .filter(Boolean)
        .join(' — ')}
      className={cn(
        'group/card relative w-full rounded-lg border border-transparent border-l-[3px] text-left text-[11px] leading-snug shadow-[0_1px_0_rgba(0,0,0,0.03)] transition-[background-color,box-shadow,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
        style.card,
        style.tint,
        late && !done && 'border-l-destructive',
        compact ? 'px-1.5 py-1' : 'px-2 py-1.5',
        draggable ? 'cursor-grab touch-none hover:border-border hover:shadow-md active:cursor-grabbing' : 'cursor-pointer hover:border-border',
        isDragging && 'opacity-40 ring-2 ring-primary/40',
        done && 'opacity-60',
      )}
    >
      {/* What it is */}
      <span className='flex items-start gap-1.5'>
        <TypeBadge code={activity.type} className='mt-px' />
        <span className='min-w-0 flex-1'>
          {tag && (
            <span className={cn('mr-1 inline-flex rounded px-1 text-[9px] font-semibold align-[1px]', TAG_TONE[activity.state])}>
              {tag}
            </span>
          )}
          <span
            className={cn(
              'font-medium text-foreground',
              compact ? 'line-clamp-1' : 'line-clamp-2',
              done && 'line-through',
            )}
          >
            {activity.description}
          </span>
        </span>
        {draggable ? (
          <GripVertical className='mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground/40 group-hover/card:text-muted-foreground' aria-hidden />
        ) : (
          !done && (
            <Lock
              className='mt-0.5 h-3 w-3 shrink-0 text-muted-foreground/50'
              aria-label={activity.movable ? 'Read only' : 'Planned in its own screen'}
            />
          )
        )}
      </span>

      {!compact && (
        <>
          {/* What it is about */}
          {(activity.sourceRef || time) && (
            <span className='mt-1 flex min-w-0 items-center gap-2 text-muted-foreground'>
              {activity.sourceRef &&
                (activity.href ? (
                  <Link
                    to={activity.href}
                    onClick={stop}
                    onPointerDown={stop}
                    title='Open the record'
                    className='min-w-0 truncate font-medium text-primary underline-offset-2 hover:underline'
                  >
                    {activity.sourceRef}
                  </Link>
                ) : (
                  <span className='min-w-0 truncate'>{activity.sourceRef}</span>
                ))}
              {time && <span className='ml-auto shrink-0 tabular-nums'>{time}</span>}
            </span>
          )}

          {/* The numbers */}
          <span className='mt-1.5 flex flex-wrap items-center gap-1'>
            <span className={cn(pill, late ? 'bg-destructive/10 text-destructive' : 'bg-card/80 text-muted-foreground ring-1 ring-border/60')} title='Due (month/year)'>
              D {formatDueMonth(activity.dueDate)}
            </span>
            <span className={cn(pill, 'bg-card/80 text-foreground ring-1 ring-border/60')} title='Expected hours'>
              {formatHours(activity.hours)} h
            </span>
            {late && (
              <span className={cn(pill, 'gap-1 bg-destructive text-white')}>
                <Clock className='h-2.5 w-2.5' /> Late
              </span>
            )}
          </span>
        </>
      )}

      {/* Quick actions, on hover or keyboard focus within the card */}
      {(onMove && draggable) || activity.href ? (
        <span className='pointer-events-none absolute right-1 top-1 flex gap-0.5 opacity-0 transition-opacity group-focus-within/card:pointer-events-auto group-focus-within/card:opacity-100 group-hover/card:pointer-events-auto group-hover/card:opacity-100'>
          {onMove && draggable && (
            <button
              type='button'
              title='Change day or owner'
              aria-label='Change day or owner'
              onClick={(e) => {
                e.stopPropagation()
                onMove(activity)
              }}
              onPointerDown={stop}
              className='flex h-5 w-5 items-center justify-center rounded-md bg-card text-muted-foreground shadow-sm ring-1 ring-border hover:text-primary'
            >
              <ArrowRightLeft className='h-3 w-3' />
            </button>
          )}
          {activity.href && (
            <Link
              to={activity.href}
              title='Open the record'
              aria-label='Open the record'
              onClick={stop}
              onPointerDown={stop}
              className='flex h-5 w-5 items-center justify-center rounded-md bg-card text-muted-foreground shadow-sm ring-1 ring-border hover:text-primary'
            >
              <ExternalLink className='h-3 w-3' />
            </Link>
          )}
        </span>
      ) : null}
    </div>
  )
}
